import type { LoadableItems } from '@trimble/data-loading';
import type {
  AlignmentItem,
  FileFolderItem,
  OrthoImageItem,
  PointCloudItem,
} from '@trimble/project-data-panel-types';
import type { ProjectDataRepository } from '@trimble/project-data-repository';
import type {
  TreeItemContextMenuOption,
  TreeNodeDefinition,
  TreeNodeStatus,
} from '@trimble/ui-components/solidjs';
import type { TieringFeatures } from '@trimble/types';
import type { Accessor, JSX } from 'solid-js';
import type { ProjectDataFilterMode } from '../project-data.types';

/** Known first-party section ids; hosts may use arbitrary strings for custom sections. */
export type ProjectDataSectionKind =
  | 'models'
  | 'pointClouds'
  | 'alignments'
  | 'orthoImages'
  | 'trajectories'
  | 'missions'
  | 'surfaces';

export interface ProjectDataSectionConfig {
  readonly id: string;
  readonly order?: number;
  readonly multiSelect?: boolean;
  readonly initiallyExpanded?: boolean;
  readonly tiering?: TieringFeatures;
}

export interface ResolvedProjectDataSectionConfig
  extends ProjectDataSectionConfig {
  readonly multiSelect: boolean;
}

export interface ProjectDataPanelConfig {
  readonly sections: readonly ProjectDataSectionConfig[];
  readonly initialFilterMode?: ProjectDataFilterMode;
  /** Filter modes to offer. An empty list hides the filter dropdown. */
  readonly filterOptions?: readonly ProjectDataFilterMode[];
  readonly panelTitle?: string;
}

/**
 * Host-supplied lazy children under repository FILE nodes (ADR-009 / ADR-016).
 * Optional on `operations.models`; wired to `ModelTree` when present. No effect when omitted.
 */
export interface ProjectDataModelLazyExpandAdapter {
  isLazyExpandable?(nodeId: string): boolean;
  /** Whether a lazy row is reachable for the Active filter (ancestors expanded); not the row's eye state. */
  isLazyNodeVisible?(nodeId: string): boolean;
  loadChildren(
    nodeId: string
  ): Promise<readonly TreeNodeDefinition<FileFolderItem>[]>;
  loadChildrenAtomically?(nodeId: string): boolean;
  ownsLazyNode?(nodeId: string): boolean;
  getCachedNodes?(): readonly TreeNodeDefinition<FileFolderItem>[];
  getExpandedNodeIds?(): readonly string[];
  onNodeExpand?(nodeId: string): void;
  onNodeCollapse?(nodeId: string): void;
}

export interface ProjectDataModelFolderActionAvailability {
  readonly loadAll: boolean;
  readonly unloadAll: boolean;
  readonly cancelLoad: boolean;
}

export interface ProjectDataModelSectionOperations {
  lazyExpand?: ProjectDataModelLazyExpandAdapter;
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  isNodeLoadFailed?(nodeId: string): boolean;
  isNodeOldVersion?(nodeId: string): boolean;
  hasNodeSubmodelChildren?(nodeId: string): boolean;
  canDownload?(item: FileFolderItem): boolean;
  canEditPosition?(item: FileFolderItem): boolean;
  getFolderActionAvailability?(
    item: FileFolderItem
  ): ProjectDataModelFolderActionAvailability;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  onRetryLoad?(item: FileFolderItem): void;
  onFitToView?(item: FileFolderItem): void;
  onShowOnly?(item: FileFolderItem): void;
  onSeeDetails?(item: FileFolderItem): void;
  onLoadLatestVersion?(item: FileFolderItem): void;
  onDownload?(item: FileFolderItem): void;
  onEditPosition?(item: FileFolderItem): void;
  onFolderLoadAll?(item: FileFolderItem): void;
  onFolderUnloadAll?(item: FileFolderItem): void;
  onFolderCancelLoad?(item: FileFolderItem): void;
  onAddToModelSpace?(item: FileFolderItem): void;
  getValidationContextMenuOptions?(
    item: FileFolderItem
  ): TreeItemContextMenuOption[];
  getTrailingContextMenuOptions?(
    item: FileFolderItem
  ): TreeItemContextMenuOption[];
  getItemTrailingContent?(item: FileFolderItem): JSX.Element | null | undefined;
  getValidationNodeStatus?(item: FileFolderItem): TreeNodeStatus | undefined;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<FileFolderItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataPointCloudsSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onCancelVisibilityToggle?(nodeId: string): void;
  onFitToView?(item: PointCloudItem): void;
  onRenderingSettings?(item: PointCloudItem): void;
  onSeeDetails?(item: PointCloudItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<PointCloudItem>,
    event: MouseEvent
  ): void;
  onShowOnly?(item: PointCloudItem): void;
  getTrailingContextMenuOptions?(
    item: PointCloudItem
  ): TreeItemContextMenuOption[];
}

export interface ProjectDataAlignmentsSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onFitToView?(item: AlignmentItem): void;
  onShowOnly?(item: AlignmentItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<AlignmentItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataOrthoImagesSectionOperations {
  isNodeLoading(nodeId: string): boolean;
  isNodeVisible(nodeId: string): boolean;
  onVisibilityToggle(nodeId: string): void;
  onDownload?(item: OrthoImageItem): void;
  onSeeDetails?(item: OrthoImageItem): void;
  onShowOnly?(item: OrthoImageItem): void;
  onDelete?(item: OrthoImageItem): void;
  isNodeSelected?(nodeId: string): boolean;
  onNodeSelect?(
    nodeId: string,
    node: TreeNodeDefinition<OrthoImageItem>,
    event: MouseEvent
  ): void;
}

export interface ProjectDataSectionOperationsMap {
  readonly models: ProjectDataModelSectionOperations;
  readonly pointClouds: ProjectDataPointCloudsSectionOperations;
  readonly alignments: ProjectDataAlignmentsSectionOperations;
  readonly orthoImages: ProjectDataOrthoImagesSectionOperations;
  readonly trajectories: object;
  readonly missions: object;
  readonly surfaces: object;
}

/**
 * Per-section host data for configured mode. Models use top-level `repository` instead of
 * `sectionData.models`; entries here are for trees that take `LoadableItems` props.
 */
export interface ProjectDataSectionDataMap {
  readonly models: undefined;
  readonly pointClouds: LoadableItems<PointCloudItem>;
  readonly alignments: LoadableItems<AlignmentItem>;
  readonly orthoImages: LoadableItems<OrthoImageItem>;
  readonly trajectories: unknown;
  readonly missions: unknown;
  readonly surfaces: unknown;
}

export type ProjectDataPanelOperations = {
  readonly [TKind in ProjectDataSectionKind]?: ProjectDataSectionOperationsMap[TKind];
};

export type ProjectDataPanelSectionData = {
  readonly [TKind in ProjectDataSectionKind]?: ProjectDataSectionDataMap[TKind];
};

export interface ProjectDataSectionRenderContext<
  TData = unknown,
  TOperations = unknown,
> {
  readonly repository: ProjectDataRepository;
  readonly config: ResolvedProjectDataSectionConfig;
  readonly data: Accessor<TData | undefined>;
  readonly operations: TOperations;
}

/**
 * SolidJS section plug-in passed explicitly at the composition root (panel, WC, harness).
 * Not a framework-neutral contract — hosts wrap this API in Solid or a Solid-based WC.
 */
export interface ProjectDataSectionDefinition<
  TData = unknown,
  TOperations = unknown,
> {
  readonly id: string;
  readonly requiresData?: boolean;
  readonly defaultMultiSelect?: boolean;
  render(
    context: ProjectDataSectionRenderContext<TData, TOperations>
  ): JSX.Element;
}

export type ProjectDataSectionDefinitionMap = Readonly<
  Record<string, ProjectDataSectionDefinition>
>;

export function createProjectDataSectionDefinitionMap(
  definitions: readonly ProjectDataSectionDefinition[]
): ProjectDataSectionDefinitionMap {
  const map: Record<string, ProjectDataSectionDefinition> = {};
  for (const definition of definitions) {
    if (map[definition.id]) {
      throw new Error(
        `Project data section "${definition.id}" is defined more than once.`
      );
    }
    map[definition.id] = definition;
  }
  return map;
}

export function resolveProjectDataSectionConfig(
  section: ProjectDataSectionConfig,
  definition: ProjectDataSectionDefinition
): ResolvedProjectDataSectionConfig {
  return {
    ...section,
    multiSelect: section.multiSelect ?? definition.defaultMultiSelect ?? false,
  };
}

export function assertSingleSelectOperationsConfigured(
  sectionId: string,
  multiSelect: boolean,
  operations: NonNullable<
    ProjectDataPanelOperations[keyof ProjectDataPanelOperations]
  >
): void {
  if (multiSelect) {
    return;
  }
  const hasSelectionOperations =
    'isNodeSelected' in operations &&
    typeof operations.isNodeSelected === 'function' &&
    'onNodeSelect' in operations &&
    typeof operations.onNodeSelect === 'function';
  if (!hasSelectionOperations) {
    throw new Error(
      `Project data section "${sectionId}" is single-select but operations.isNodeSelected and operations.onNodeSelect are required.`
    );
  }
}

export function orderProjectDataSections(
  sections: readonly ProjectDataSectionConfig[]
): readonly ProjectDataSectionConfig[] {
  return sections
    .map((section, index) => ({ section, index }))
    .sort(
      (left, right) =>
        (left.section.order ?? Number.MAX_SAFE_INTEGER) -
          (right.section.order ?? Number.MAX_SAFE_INTEGER) ||
        left.index - right.index
    )
    .map(({ section }) => section);
}

export function assertUniqueProjectDataSectionIds(
  sections: readonly ProjectDataSectionConfig[]
): void {
  const ids = new Set<string>();
  for (const section of sections) {
    if (ids.has(section.id)) {
      throw new Error(
        `Project data section "${section.id}" is configured more than once.`
      );
    }
    ids.add(section.id);
  }
}
